import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, Button, TextField, Typography, Paper, Alert, CircularProgress,
  Card, CardContent, Chip, Stack, Divider,
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import { requestPortalAccess, portalLogin, getPortalAppointments, cancelPortalAppointment } from '../services/api';
import AppointmentTypeChip from '../components/AppointmentTypeChip';

// Sesión de portal separada de la de staff — su propia key en
// sessionStorage (no localStorage: no tiene sentido que sobreviva más
// que la pestaña, la sesión ya dura poco del lado del backend, 30 min).
const PORTAL_TOKEN_KEY = 'portal_token';

const STATE_LABELS = {
  libre: 'Libre', pendiente: 'Pendiente', confirmado: 'Confirmado',
  en_consulta: 'En consulta', cancelado: 'Cancelado', ausente: 'Ausente', atendido: 'Atendido',
};
const STATE_COLORS = {
  pendiente: 'error', confirmado: 'warning', en_consulta: 'success', cancelado: 'default',
  ausente: 'warning', atendido: 'info', libre: 'default',
};

const asUtc = (d) => new Date(typeof d === 'string' && !d.endsWith('Z') ? d + 'Z' : d);
const fmtDateTime = (d) => asUtc(d).toLocaleString('es-AR', {
  day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
});

// ── Pedir acceso ─────────────────────────────────────────────────────────────
const RequestAccessForm = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await requestPortalAccess(email);
      setDone(true);
    } catch (err) {
      setError(err.userMessage || 'Error al enviar el link. Intentá de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper sx={{ maxWidth: 420, width: '100%', p: 4, borderRadius: 2 }}>
      <Typography variant="h5" fontWeight={700} mb={0.5}>Mis turnos</Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Ingresá el email con el que te registraste en la clínica y te mandamos un link para ver tus turnos.
      </Typography>

      {done ? (
        <Alert severity="success">
          Si el email está registrado, vas a recibir un link de acceso. Revisá también tu carpeta de spam — el link vence en 15 minutos.
        </Alert>
      ) : (
        <form onSubmit={handleSubmit}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <TextField
            fullWidth label="Email" type="email" required
            value={email} onChange={e => setEmail(e.target.value)}
            sx={{ mb: 2 }} size="small"
            error={!!error}
          />
          <Button fullWidth type="submit" variant="contained" disabled={loading || !email}>
            {loading ? <CircularProgress size={20} color="inherit" /> : 'Enviar link'}
          </Button>
        </form>
      )}
    </Paper>
  );
};

// ── Lista de turnos ──────────────────────────────────────────────────────────
const AppointmentsList = ({ portalToken, onSessionExpired }) => {
  const [appointments, setAppointments] = useState(null);
  const [error, setError] = useState('');
  const [cancelingId, setCancelingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await getPortalAppointments(portalToken);
      setAppointments(res.data);
    } catch (err) {
      if (err.response?.status === 401) {
        onSessionExpired();
      } else {
        setError(err.userMessage || 'Error al cargar tus turnos');
      }
    }
  }, [portalToken, onSessionExpired]);

  useEffect(() => { load(); }, [load]);

  const handleCancel = async (appt) => {
    if (!window.confirm('¿Cancelar este turno?')) return;
    setCancelingId(appt.id);
    try {
      await cancelPortalAppointment(portalToken, appt.id);
      await load();
    } catch (err) {
      setError(err.userMessage || 'Error al cancelar el turno');
    } finally {
      setCancelingId(null);
    }
  };

  if (error) return <Alert severity="error">{error}</Alert>;
  if (appointments === null) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  }
  if (appointments.length === 0) {
    return <Paper sx={{ p: 4, textAlign: 'center' }}><Typography color="text.secondary">No tenés turnos registrados.</Typography></Paper>;
  }

  const now = new Date();
  const canCancel = (appt) => !['cancelado', 'atendido', 'ausente', 'en_consulta'].includes(appt.state) && asUtc(appt.start_time) > now;

  return (
    <Stack spacing={1.5} sx={{ width: '100%', maxWidth: 560 }}>
      {appointments.map(appt => (
        <Card key={appt.id} variant="outlined">
          <CardContent sx={{ '&:last-child': { pb: 2 } }}>
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1}>
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <EventIcon color="action" sx={{ mt: 0.25 }} />
                <Box>
                  <Typography variant="body2" fontWeight={600}>{fmtDateTime(appt.start_time)}</Typography>
                  {appt.doctor && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {appt.doctor.name}{appt.doctor.specialty ? ` — ${appt.doctor.specialty}` : ''}
                    </Typography>
                  )}
                  {appt.appointment_type && (
                    <Box sx={{ mt: 0.5 }}>
                      <AppointmentTypeChip appointmentType={appt.appointment_type} />
                    </Box>
                  )}
                </Box>
              </Box>
              <Chip label={STATE_LABELS[appt.state] || appt.state} color={STATE_COLORS[appt.state] || 'default'} size="small" />
            </Stack>
            {canCancel(appt) && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Button
                  size="small" color="error" variant="outlined"
                  disabled={cancelingId === appt.id}
                  onClick={() => handleCancel(appt)}
                >
                  {cancelingId === appt.id ? <CircularProgress size={16} /> : 'Cancelar turno'}
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      ))}
    </Stack>
  );
};

// ── Página ────────────────────────────────────────────────────────────────────
const PatientPortal = () => {
  const [searchParams] = useSearchParams();
  const urlToken = searchParams.get('token');

  const [portalToken, setPortalToken] = useState(() => sessionStorage.getItem(PORTAL_TOKEN_KEY));
  const [exchanging, setExchanging] = useState(Boolean(urlToken));
  const [exchangeError, setExchangeError] = useState('');

  useEffect(() => {
    if (!urlToken) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await portalLogin(urlToken);
        if (cancelled) return;
        sessionStorage.setItem(PORTAL_TOKEN_KEY, res.data.access_token);
        setPortalToken(res.data.access_token);
        // Limpia el token de la URL para que no quede en el historial del navegador.
        window.history.replaceState({}, '', '/portal');
      } catch (err) {
        if (!cancelled) setExchangeError(err.userMessage || 'Este link ya no es válido. Pedí uno nuevo.');
      } finally {
        if (!cancelled) setExchanging(false);
      }
    })();
    return () => { cancelled = true; };
  }, [urlToken]);

  const handleSessionExpired = () => {
    sessionStorage.removeItem(PORTAL_TOKEN_KEY);
    setPortalToken(null);
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', bgcolor: 'background.default', p: 2, pt: 6 }}>
      {exchanging ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : exchangeError ? (
        <Paper sx={{ maxWidth: 420, width: '100%', p: 4, borderRadius: 2 }}>
          <Alert severity="error" sx={{ mb: 2 }}>{exchangeError}</Alert>
          <RequestAccessForm />
        </Paper>
      ) : portalToken ? (
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Typography variant="h5" fontWeight={700} mb={2}>Mis turnos</Typography>
          <AppointmentsList portalToken={portalToken} onSessionExpired={handleSessionExpired} />
        </Box>
      ) : (
        <RequestAccessForm />
      )}
    </Box>
  );
};

export default PatientPortal;
